import axios from 'axios';

import { downloadFileFromUrl } from '@src/utils';

import { useRoomStore } from '@src/stores';

import {
  IAttendeesFilters,
  ICountryApiResponse,
  IProfileInfoResponse,
  IScheduledMeeting,
  IScheduledMeetingAPIResponse,
  IQuestionAndAnswerFilterResponse,
  IWebinar,
  TShowFilter,
  TSortFilter,
  TWebinarStatus,
  IFetchCTAsResponse,
} from '@src/types';
import {
  IAttendee,
  IAttendeeFilters,
  IDeclineInvitationResponse,
  IFetchAttendeesResponse,
  IQuickInvitePayload,
  IQuickInviteResponse,
  ISendInvitePayload,
  ISendInviteResponse,
  IWebinarAttendees,
} from '@src/webinar/types';

import client from '../client';
import { MEETING_TYPE } from '@src/constants';

export const fetchRoomData = async (roomId: string): Promise<IWebinar | null> => {
  const { data } = await client.get(`/api/v1/room?roomId=${roomId}`, { withCredentials: true });
  return data?.[0] ?? null;
};

export const checkRegistrationEnabled = async (
  roomId: string
): Promise<{
  isEnabled: boolean;
  code?: 'REGISTRATION_NOT_REQUIRED' | 'REGISTRATION_CLOSED';
}> => {
  try {
    const data: {
      isEnabled: boolean;
      data: { canRegister: boolean; code: 'REGISTRATION_NOT_REQUIRED' | 'REGISTRATION_CLOSED' };
      error?: string;
      status: number;
    } = await client.get(`/api/v1/user/registration-enabled?roomId=${roomId}`, {
      withCredentials: true,
    });
    return { isEnabled: true, code: data.data.code };
  } catch (err: any) {
    if (err.response?.status === 400 && err.response?.data?.code === 'REGISTRATION_CLOSED') {
      return { isEnabled: false };
    }

    return { isEnabled: true, code: err.response?.data?.code };
  }
};

export const fetchCountries = async (): Promise<{ label: string; value: string }[]> => {
  const response = await axios.get<ICountryApiResponse>(`${process.env.REACT_APP_OES_API_URL}/oesuser/countries`, {
    withCredentials: true,
  });
  const countries = response.data.body;
  return countries
    .map((country) => ({
      label: country.countryName,
      value: country.countryCode,
    }))
    .sort((a, b) => a.label.localeCompare(b.label));
};

export const fetchWebinarAttendees = async (id: string): Promise<IWebinarAttendees> => {
  const response = await client.get(
    `${process.env.REACT_APP_WEBINAR_ENDPOINT}/api/v1/dashboard/webinar/${id}/attendance`,
    {
      withCredentials: true,
    }
  );
  return response.data;
};

export const fetchPaginatedAttendees = async (
  roomId: string,
  page: number,
  filters?: IAttendeesFilters
): Promise<IFetchAttendeesResponse> => {
  const { company = [], country = [], domain = [], type = [], search = '', invitationStatus = '' } = filters || {};
  const { data } = await client.get('/api/v1/registered-user', {
    params: {
      roomId,
      page,
      ...(type.length && { type: type.join(',') }),
      ...(domain.length && { domain: domain.join(',') }),
      ...(country.length && { country: country.join(',') }),
      ...(company.length && { company: company.join(',') }),
      ...(search.length && { searchKeyword: search }),
      ...(invitationStatus && { invitationStatus }),
    },
    withCredentials: true,
  });

  return data;
};

export const sendWebinarInvitation = async (
  payload: ISendInvitePayload,
  isReinvite?: boolean
): Promise<ISendInviteResponse> => {
  const { data } = await client.post<ISendInviteResponse>(
    `/api/v1/user/${isReinvite ? 'reinvite' : 'invite'}`,
    payload,
    {
      withCredentials: true,
    }
  );
  return data;
};

export const generateQuickInviteLink = async (payload: IQuickInvitePayload): Promise<IQuickInviteResponse> => {
  const { data } = await client.post<IQuickInviteResponse>('/api/v1/user/quick-invite', payload, {
    withCredentials: true,
  });
  return data;
};

export const fetchWebinarAttendeesFilters = async (id: string): Promise<IAttendeeFilters> => {
  const response = await client.get(
    `${process.env.REACT_APP_WEBINAR_ENDPOINT}/api/v1/registered-users/filter-data?roomId=${id}`,

    {
      withCredentials: true,
    }
  );
  return response.data;
};

export const postWebinarAttendeeAction = async ({
  status,
  attendees,
}: {
  status: TWebinarStatus;
  attendees: IAttendee[];
}): Promise<any> => {
  const userIds = attendees.map(({ _id }) => _id);
  const payload = {
    status,
    userIds,
  };
  const response = await client.post(
    `${process.env.REACT_APP_WEBINAR_ENDPOINT}/api/v1/user/bulk-acknowledge`,
    payload,
    {
      withCredentials: true,
    }
  );
  return response.data;
};

export const postWebinarAttendeeActionByFilter = async (data: {
  roomId: string;
  searchKeyword: string;
  country: string;
  domain: string;
  company: string;
  status: { status: 'APPROVED' | 'REJECTED' };
  type: string;
}): Promise<any> => {
  const response = await client.post(
    `${process.env.REACT_APP_WEBINAR_ENDPOINT}/api/v1/user/filter-bulk-acknowledge?roomId=${data.roomId}&searchKeyword=${data.searchKeyword}&country=${data.country}&domain=${data.domain}&company=${data.company}&type=${data.type}`,
    data.status,
    {
      withCredentials: true,
    }
  );
  return response.data;
};

export const postWebinarDeclineInvitation = async (userId: string): Promise<IDeclineInvitationResponse> => {
  const response = await client.post(
    `${process.env.REACT_APP_WEBINAR_ENDPOINT}/api/v1/user/invite-decline`,
    { userId },
    {
      headers: {
        'Content-Type': 'application/json',
      },
      withCredentials: true,
    }
  );
  return response.data;
};

export const fetchProfileInfo = async (): Promise<IProfileInfoResponse> => {
  try {
    const response = await client.get(`${process.env.REACT_APP_OES_API_URL}/oesuser/api/v2/users/profile`, {
      withCredentials: true,
    });
    return response.data;
  } catch (error: any) {
    throw new Error(error?.response?.data?.message || 'Failed to fetch profile info');
  }
};

export const deleteWebinar = async (id: string) => {
  await client.delete(`${process.env.REACT_APP_WEBINAR_ENDPOINT}/api/v1/room?roomId=${id}`, {
    headers: { 'Content-Type': 'application/json' },
    withCredentials: true,
  });
};

export const fetchAllMeetingsAndWebinars = async (): Promise<IScheduledMeeting[]> => {
  const [meetingsResponse] = await Promise.all([
    client.get(`${process.env.REACT_APP_OES_API_URL}/omail/api/v2/meetings?size=10000&page=1`, {
      withCredentials: true,
    }),
  ]);

  const meetingsData = meetingsResponse?.data?.result as IScheduledMeetingAPIResponse[];

  const mappedMeetings: IScheduledMeeting[] = meetingsData
    .filter((item: any) => !!item?.roomTitle && !!item?.startTime && !!item?.endTime)
    .map((item: any) => ({
      ...item,
      id: item.roomId,
      roomTitle: item.roomTitle,
      type: item.type || MEETING_TYPE.MEETING,
      startTime: new Date(item.startTime),
      endTime: new Date(item.endTime),
      status: item.status,
    }));

  return mappedMeetings.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());
};

export const questionAndAnswerFilter = async (roomId: string, filterType: TShowFilter, sortOrder: TSortFilter) => {
  const response = await client.get(`${process.env.REACT_APP_WEBINAR_ENDPOINT}/api/v1/questions?roomId=${roomId}`, {
    params: {
      filterType,
      sortOrder,
    },
    headers: {
      'x-access-key': useRoomStore.getState().accessKey || '',
    },
    withCredentials: true,
  });
  return response.data;
};

export const fetchQuestionWithReplies = async (roomId: string, questionId: string) => {
  const response = await client.get(
    `${process.env.REACT_APP_WEBINAR_ENDPOINT}/api/v1/question?questionId=${questionId}`,
    {
      params: {
        roomId,
        time: Date.now(), // to avoid caching
      },
      headers: {
        'x-access-key': useRoomStore.getState().accessKey || '',
      },
      withCredentials: true,
    }
  );
  return response.data;
};

export const fetchMyQuestions = async (roomId: string) => {
  const response: { data: IQuestionAndAnswerFilterResponse } = await client.get(
    `${process.env.REACT_APP_WEBINAR_ENDPOINT}/api/v1/questions?roomId=${roomId}`,
    {
      params: {
        filterType: 'MY_QUESTIONS',
      },
      headers: {
        'x-access-key': useRoomStore.getState().accessKey || '',
      },
      withCredentials: true,
    }
  );
  return response.data.result.map(({ id }) => id);
};

export const exportQuestionAndAnswerCSV = async (roomId: string): Promise<void> => {
  // First, get the file URL from the API
  const response = await client.get<{ success: boolean; fileUrl: string }>(
    `${process.env.REACT_APP_WEBINAR_ENDPOINT}/api/v1/webinar/${roomId}/export`,
    {
      headers: {
        'x-access-key': useRoomStore.getState().accessKey || '',
      },
      withCredentials: true,
    }
  );

  const { fileUrl } = response.data;

  // Download the file from the S3 URL
  await downloadFileFromUrl(fileUrl);
};

export const fetchCTAs = async (roomId: string): Promise<IFetchCTAsResponse> => {
  const response = await client.get(`${process.env.REACT_APP_WEBINAR_ENDPOINT}/api/v1/ctas`, {
    params: { roomId },
    withCredentials: true,
  });

  return response.data;
};
